import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiPlus, FiTrash2, FiEye, FiSave, FiUpload, FiPackage, FiTruck, FiUser, FiMapPin, FiTarget, FiRuler, FiChevronDown, FiChevronRight, FiDownload } from 'react-icons/fi';
import { shipmentsAPI } from '../services/api';
import { toast } from 'react-toastify';

const CreateShipment = () => {
  const [searchParams] = useSearchParams();
  const [shipmentType, setShipmentType] = useState('manual'); // 'manual' or 'csv'
  const [masterShipment, setMasterShipment] = useState({
    // Master shipment will just get an auto-generated ID
  });
  const [units, setUnits] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expandedUnits, setExpandedUnits] = useState(new Set());
  const [isFromCSV, setIsFromCSV] = useState(false);

  // Check URL parameters on component mount
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'csv') {
      setShipmentType('csv');
    }
  }, [searchParams]);

  // Add new unit
  const addUnit = () => {
    const newUnit = {
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip: '',
      country: 'US',
      weight_gms: '',
      length_cm: '',
      width_cm: '',
      height_cm: '',
      reference_id: '',
      item_description: '',
      hsn_code: '',
      item_value: ''
    };
    setUnits([...units, newUnit]);
  };

  // Remove unit
  const removeUnit = (unitIndex) => {
    setUnits(units.filter((_, i) => i !== unitIndex));
    // Update expanded units indices
    const newExpanded = new Set();
    expandedUnits.forEach(expandedIndex => {
      if (expandedIndex < unitIndex) {
        newExpanded.add(expandedIndex);
      } else if (expandedIndex > unitIndex) {
        newExpanded.add(expandedIndex - 1);
      }
    });
    setExpandedUnits(newExpanded);
  };

  // Toggle unit expansion
  const toggleUnitExpansion = (unitIndex) => {
    const newExpanded = new Set();
    if (!expandedUnits.has(unitIndex)) {
      // If clicking on a collapsed unit, expand only this one
      newExpanded.add(unitIndex);
    }
    // If clicking on an expanded unit, collapse it (newExpanded stays empty)
    setExpandedUnits(newExpanded);
  };

  // Update unit
  const updateUnit = (unitIndex, field, value) => {
    const updated = [...units];
    updated[unitIndex][field] = value;
    setUnits(updated);
  };


  // Validate form
  const validateForm = () => {
    if (units.length === 0) {
      toast.error('Please add at least one unit');
      return false;
    }

    for (let unitIndex = 0; unitIndex < units.length; unitIndex++) {
      const unit = units[unitIndex];
      if (!unit.customer_name || !unit.address_line1 || 
          !unit.city || !unit.state || !unit.zip || !unit.weight_gms) {
        toast.error(`Please fill in all required fields for unit ${unitIndex + 1}`);
        return false;
      }
    }

    return true;
  };

  // Save shipment
  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const shipmentData = {
        master_shipment: {}, // Empty - will be auto-generated
        units: units // Send units directly, no boxes
      };

      await shipmentsAPI.createShipment(shipmentData);
      toast.success('Shipment created successfully!');
      
      // Reset form
      setMasterShipment({});
      setUnits([]);
      setExpandedUnits(new Set());
      setShowPreview(false);
      setIsFromCSV(false);
    } catch (error) {
      toast.error('Failed to create shipment');
      console.error('Error creating shipment:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse CSV file to units
  const parseCSV = (csvText) => {
    const lines = csvText.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];

    // Parse CSV properly handling quoted fields
    const parseCSVLine = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      result.push(current.trim());
      return result;
    };

    const headers = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) {
        console.warn(`Row ${i + 1}: Expected ${headers.length} columns, got ${values.length}`);
        continue;
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      data.push(row);
    }

    return data; // Return units directly, no grouping by boxes
  };

  // Download sample CSV
  const downloadSampleCSV = () => {
    const sampleData = [
      {
        customer_name: 'John Doe',
        customer_email: 'john.doe@example.com',
        customer_phone: '+1234567890',
        address_line1: '123 Main St',
        address_line2: 'Apt 4B',
        city: 'New York',
        state: 'NY',
        zip: '10001',
        country: 'US',
        weight_gms: '500',
        length_cm: '30',
        width_cm: '20',
        height_cm: '15',
        reference_id: 'REF001',
        item_description: 'Electronics',
        hsn_code: '85171200',
        item_value: '299.99'
      },
      {
        customer_name: 'Jane Smith',
        customer_email: 'jane.smith@example.com',
        customer_phone: '+1234567891',
        address_line1: '456 Oak Ave',
        address_line2: '',
        city: 'Los Angeles',
        state: 'CA',
        zip: '90210',
        country: 'US',
        weight_gms: '750',
        length_cm: '25',
        width_cm: '15',
        height_cm: '10',
        reference_id: 'REF002',
        item_description: 'Books',
        hsn_code: '49019900',
        item_value: '45.50'
      },
      {
        customer_name: 'Bob Johnson',
        customer_email: 'bob.johnson@example.com',
        customer_phone: '+1234567892',
        address_line1: '789 Pine St',
        address_line2: 'Suite 200',
        city: 'Chicago',
        state: 'IL',
        zip: '60601',
        country: 'US',
        weight_gms: '300',
        length_cm: '20',
        width_cm: '15',
        height_cm: '5',
        reference_id: 'REF003',
        item_description: 'Clothing',
        hsn_code: '62034200',
        item_value: '89.99'
      },
      {
        customer_name: 'Alice Brown',
        customer_email: 'alice.brown@example.com',
        customer_phone: '+1234567893',
        address_line1: '321 Elm St',
        address_line2: '',
        city: 'Miami',
        state: 'FL',
        zip: '33101',
        country: 'US',
        weight_gms: '600',
        length_cm: '35',
        width_cm: '25',
        height_cm: '20',
        reference_id: 'REF004',
        item_description: 'Home Goods',
        hsn_code: '94019000',
        item_value: '150.00'
      }
    ];

    // Create CSV content
    const headers = Object.keys(sampleData[0]);
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => headers.map(header => row[header] || '').join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_shipments.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Sample CSV downloaded successfully!');
  };

  // Handle CSV upload
  const handleCSVUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const csvText = await file.text();
      const csvData = parseCSV(csvText);
      
      if (csvData.length === 0) {
        toast.error('No valid data found in CSV file');
        return;
      }

      // Map CSV data to units format
      const mappedUnits = csvData.map((row) => ({
        customer_name: row.customer_name || row.name || row.recipient_name || '',
        customer_email: row.customer_email || row.email || row.recipient_email || '',
        customer_phone: row.customer_phone || row.phone || row.recipient_phone || '',
        address_line1: row.address_line1 || row.address || row.street || '',
        address_line2: row.address_line2 || row.address2 || '',
        city: row.city || '',
        state: row.state || row.province || '',
        zip: row.zip || row.postal_code || row.zipcode || '',
        country: row.country || 'US',
        weight_gms: row.weight_gms || row.weight || row.weight_grams || '',
        length_cm: row.length_cm || row.length || '',
        width_cm: row.width_cm || row.width || '',
        height_cm: row.height_cm || row.height || '',
        reference_id: row.reference_id || row.ref_id || row.reference || '',
        item_description: row.item_description || row.description || row.item || '',
        hsn_code: row.hsn_code || row.hsn || row.harmonized_code || '',
        item_value: row.item_value || row.value || row.price || row.cost || ''
      }));

      // Set the units and switch to manual mode for editing
      setUnits(mappedUnits);
      setShipmentType('manual');
      setExpandedUnits(new Set([0])); // Expand only first unit, others will be collapsed
      setIsFromCSV(true); // Mark as from CSV
      
      toast.success(`CSV parsed successfully! ${mappedUnits.length} units loaded. You can now review and edit before saving.`);
      
      // Reset file input
      event.target.value = '';
    } catch (error) {
      toast.error('Failed to parse CSV file');
      console.error('Error parsing CSV:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Orders</h1>
          <p className="text-gray-600">Upload your daily orders for shipment processing</p>
        </div>
      </div>

      {/* Shipment Type Selection */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Choose Creation Method</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setShipmentType('manual')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              shipmentType === 'manual'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <FiPackage className="w-8 h-8 text-blue-600 mb-2" />
            <h4 className="font-medium text-gray-900">Manual Entry</h4>
            <p className="text-sm text-gray-600">Create shipment step by step</p>
          </button>
          
          <button
            onClick={() => setShipmentType('csv')}
            className={`p-4 border-2 rounded-lg text-left transition-colors ${
              shipmentType === 'csv'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <FiUpload className="w-8 h-8 text-blue-600 mb-2" />
            <h4 className="font-medium text-gray-900">CSV Upload</h4>
            <p className="text-sm text-gray-600">Upload shipment data from file</p>
          </button>
        </div>
      </div>

      {shipmentType === 'manual' ? (
        <>
          {/* Master Shipment Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-medium text-blue-900 mb-2 flex items-center">
              <FiTruck className="w-5 h-5 mr-2" />
              Master Shipment
            </h3>
            <p className="text-blue-700">
              A master shipment ID will be automatically generated when you save. This represents the consolidated package for your shipment.
            </p>
          </div>

          {/* Units */}
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <FiPackage className="w-5 h-5 mr-2" />
                  Order Items ({units.length} units)
                </h3>
                {isFromCSV && (
                  <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <FiUpload className="w-3 h-3 mr-1" />
                    From CSV
                  </span>
                )}
              </div>
            </div>

            {isFromCSV && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                <div className="flex">
                  <FiUpload className="w-5 h-5 text-green-400 mr-2 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-800">
                      <strong>CSV data loaded successfully!</strong> Review and edit the shipments below before saving. 
                      You can modify any field or add additional shipments.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {units.map((unit, unitIndex) => {
              const isExpanded = expandedUnits.has(unitIndex);
              const hasData = unit.customer_name || unit.customer_email || unit.address_line1;
              
              return (
                <div key={unitIndex} className="border border-gray-200 rounded-lg mb-4">
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleUnitExpansion(unitIndex)}
                  >
                    <div className="flex items-center">
                      {isExpanded ? (
                        <FiChevronDown className="w-5 h-5 text-gray-500 mr-2" />
                      ) : (
                        <FiChevronRight className="w-5 h-5 text-gray-500 mr-2" />
                      )}
                      <h4 className="font-medium text-gray-900">
                        Unit #{unitIndex + 1}
                        {hasData && (
                          <span className="ml-2 text-sm text-gray-500">
                            - {unit.customer_name || 'Unnamed'}
                          </span>
                        )}
                      </h4>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeUnit(unitIndex);
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Remove unit"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Customer Name *
                          </label>
                          <input
                            type="text"
                            value={unit.customer_name}
                            onChange={(e) => updateUnit(unitIndex, 'customer_name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="John Doe"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <input
                            type="email"
                            value={unit.customer_email}
                            onChange={(e) => updateUnit(unitIndex, 'customer_email', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="john@example.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Phone
                          </label>
                          <input
                            type="tel"
                            value={unit.customer_phone}
                            onChange={(e) => updateUnit(unitIndex, 'customer_phone', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="+1234567890"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 1 *
                          </label>
                          <input
                            type="text"
                            value={unit.address_line1}
                            onChange={(e) => updateUnit(unitIndex, 'address_line1', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="123 Main Street"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address Line 2
                          </label>
                          <input
                            type="text"
                            value={unit.address_line2}
                            onChange={(e) => updateUnit(unitIndex, 'address_line2', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Apt 4B"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City *
                          </label>
                          <input
                            type="text"
                            value={unit.city}
                            onChange={(e) => updateUnit(unitIndex, 'city', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="New York"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State *
                          </label>
                          <input
                            type="text"
                            value={unit.state}
                            onChange={(e) => updateUnit(unitIndex, 'state', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="NY"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            ZIP Code *
                          </label>
                          <input
                            type="text"
                            value={unit.zip}
                            onChange={(e) => updateUnit(unitIndex, 'zip', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="10001"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country
                          </label>
                          <select
                            value={unit.country}
                            onChange={(e) => updateUnit(unitIndex, 'country', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                            <option value="MX">Mexico</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Weight (grams) *
                          </label>
                          <input
                            type="number"
                            value={unit.weight_gms}
                            onChange={(e) => updateUnit(unitIndex, 'weight_gms', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="500"
                            min="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Length (cm)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={unit.length_cm}
                            onChange={(e) => updateUnit(unitIndex, 'length_cm', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="30"
                            min="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Width (cm)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={unit.width_cm}
                            onChange={(e) => updateUnit(unitIndex, 'width_cm', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="20"
                            min="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Height (cm)
                          </label>
                          <input
                            type="number"
                            step="0.1"
                            value={unit.height_cm}
                            onChange={(e) => updateUnit(unitIndex, 'height_cm', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="15"
                            min="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Reference ID
                          </label>
                          <input
                            type="text"
                            value={unit.reference_id}
                            onChange={(e) => updateUnit(unitIndex, 'reference_id', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="REF123456"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Item Description
                          </label>
                          <input
                            type="text"
                            value={unit.item_description}
                            onChange={(e) => updateUnit(unitIndex, 'item_description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Electronics, Books, etc."
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            HSN Code
                          </label>
                          <input
                            type="text"
                            value={unit.hsn_code}
                            onChange={(e) => updateUnit(unitIndex, 'hsn_code', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="85171200"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Item Value (USD)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={unit.item_value}
                            onChange={(e) => updateUnit(unitIndex, 'item_value', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="299.99"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {units.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FiPackage className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No units added yet. Click "Add Unit" below to get started.</p>
              </div>
            )}

            {/* Add Unit Button - Bottom */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={addUnit}
                className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 transition-colors"
              >
                <FiPlus className="w-4 h-4 mr-2" />
                Add Unit
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setShowPreview(true)}
              disabled={units.length === 0}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiEye className="w-4 h-4 mr-2" />
              Preview Orders
            </button>
            
            <button
              onClick={handleSave}
              disabled={loading || units.length === 0}
              className="flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSave className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Upload Orders'}
            </button>
          </div>
        </>
      ) : (
        /* CSV Upload Section */
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload CSV File</h3>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <FiUpload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 mb-4">Upload your shipment data in CSV format</p>
            <p className="text-sm text-gray-500 mb-6">
              After upload, you'll be able to review and edit all shipments before saving
            </p>
            
            <input
              type="file"
              accept=".csv"
              onChange={handleCSVUpload}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 cursor-pointer"
            >
              <FiUpload className="w-4 h-4 mr-2" />
              Choose CSV File
            </label>
            
            <div className="mt-6 text-left bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">CSV Format Requirements:</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Required columns:</strong> customer_name, address_line1, city, state, zip, weight_gms</p>
                <p><strong>Optional columns:</strong> customer_email, customer_phone, address_line2, country, length_cm, width_cm, height_cm, reference_id, item_description, hsn_code, item_value</p>
                <p><strong>Alternative names:</strong> name/recipient_name, email/recipient_email, address/street, postal_code/zipcode, hsn/harmonized_code, value/price/cost</p>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-2">Need a template to get started?</p>
                <button
                  onClick={downloadSampleCSV}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
                >
                  <FiDownload className="w-4 h-4 mr-2" />
                  Download Sample CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Orders Preview</h3>
              
              {/* Master Shipment Preview */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">Master Shipment</h4>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-blue-700">
                    <strong>Master ID:</strong> Will be auto-generated (e.g., MS12345678)
                  </p>
                  <p className="text-blue-600 text-sm mt-1">
                    This represents the consolidated package for your shipment.
                  </p>
                </div>
              </div>

              {/* Units Preview */}
              <div className="mb-6">
                <h4 className="font-medium text-gray-900 mb-2">
                  Order Items ({units.length} units)
                </h4>
                {units.map((unit, unitIndex) => (
                  <div key={unitIndex} className="bg-white p-3 rounded border-l-4 border-blue-200 mb-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div><strong>Customer:</strong> {unit.customer_name}</div>
                      <div><strong>Email:</strong> {unit.customer_email || 'Not provided'}</div>
                      <div className="col-span-2"><strong>Address:</strong> {unit.address_line1}, {unit.city}, {unit.state} {unit.zip}</div>
                      <div><strong>Weight:</strong> {unit.weight_gms}g</div>
                      <div><strong>Dimensions:</strong> {unit.length_cm}×{unit.width_cm}×{unit.height_cm}cm</div>
                      <div><strong>Item:</strong> {unit.item_description}</div>
                      <div><strong>HSN Code:</strong> {unit.hsn_code || 'Not specified'}</div>
                      <div><strong>Value:</strong> ${unit.item_value || '0.00'}</div>
                      <div><strong>Reference:</strong> {unit.reference_id || 'Not specified'}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowPreview(false);
                    handleSave();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Upload Orders
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateShipment;
